import { Button } from "@msuite/ui/button";
import type { IFileExplorerContext } from "@msuite/ui/files";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { isOfferImportFileName } from "@msuite/utils/files";
import type { Tables } from "@msuite/utils/types";
import { Fragment } from "react";
import { useCitadel } from "@/hooks/use-citadel";
import { ImportOfferFilesModal } from "@/modals/calculations/import-offer-files-modal";

/** Props Interface */
interface ImportOfferActionProps {
	context: IFileExplorerContext<{ file: Tables<"files"> }>;
}

/**
 * Raise a calculation from the Angebot sitting in a document folder.
 *
 * An offer the classifier recognised is filed onto its project, which is where it belongs and is
 * also where it stops being reachable: the inbox only ever holds what nothing could place. So the
 * import is offered here as well, over the selection rather than over a row, because a GAEB
 * Leistungsverzeichnis arrives as a pair of files and one of them alone imports as a column of
 * prices with no positions to attach them to.
 *
 * It appears only while every ticked file is one the importer has an extractor for. A selection
 * holding a photograph would import the offers beside it and silently drop the rest, and a button
 * that quietly does less than the selection says is worse than one that is not there.
 */
export function ImportOfferAction({ context }: ImportOfferActionProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const modal = useDisclosure();

	/** Query */
	const { citadel } = useCitadel();

	/** Computed */
	const { selectedFiles, filesById, refetch } = context;
	// Resolved through `filesById` rather than through the rendered tree: a file ticked before the
	// search box was typed into is still selected and no longer on screen.
	const isImportable =
		selectedFiles.length > 0 &&
		selectedFiles.every((fileId) => isOfferImportFileName(filesById.get(fileId)?.fileName));

	/** Guard */
	if (!isImportable || citadel?.can("create", "Calculation") !== true) return null;

	/** Render */
	return (
		<Fragment>
			<Button
				icon={<Icon name="Calculator" />}
				onClick={modal.onOpen}
			>
				{t("calculations.import_offer")}
			</Button>
			{modal.isOpen && (
				<ImportOfferFilesModal
					isOpen={modal.isOpen}
					onClose={modal.onClose}
					onComplete={refetch}
					fileIds={selectedFiles}
				/>
			)}
		</Fragment>
	);
}
